import {
    forwardRef,
    Fragment,
    useImperativeHandle,
    useRef,
    useState,
    type ReactNode,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import XCircleIcon from "@heroicons/react/20/solid/XCircleIcon";
import InformationCircleIcon from "@heroicons/react/24/solid/InformationCircleIcon";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import { prop, uniqBy } from "ramda";

import cx from "@hsl/core/utils/cx";
import { getShareClassId } from "@hsl/fc-subscription/lib";
import {
    type FundDataWithType,
    type ShareClassDataWithType,
} from "@hsl/fund-explorer/metaSchemas";

import { Translate } from "../../../../../hooks/useTranslation";
import groupShareClasses from "../../../../../lib/groupShareClasses--temp";
import { type SubscribableAsset } from "../../../../../openapi";
import Button from "../../../../Button";
import Checkbox from "../../../../Checkbox";
import { ControlledCollapsible } from "../../../../Collapsible";
import { useSelectDocumentContext } from "./context";
import { type Frequency } from "./FrequencySelector";
import { getDataWithTypeId as toId } from "./helpers";
import SelectAllToggle from "./SelectAllToggle";
import type { SelectionDocument } from "./types";

type Props = {
    doc: SelectionDocument;
    funds: FundDataWithType[];
    shareClasses: ShareClassDataWithType[];
    frequency: Frequency;
    onClose?(): void;
    onOpen?(): void;
    onChange?(shareClasses: string | string[], selected: boolean): void;
    onSave?(): void;
};

type FundSelectionCount = Map<string, { count: number }>;

export default function ShareClassSelector({
    doc,
    funds,
    shareClasses,
    frequency,
    onClose,
    onOpen,
    onChange,
    onSave,
}: Props) {
    const fundIds = funds.map(toId);
    const shareClassIds = shareClasses.map(toId);
    const [isOpen, setIsOpen] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const refs = useRef<FundSelectionCount>();
    const [errKey, setErrKey] = useState<string>(`err-key-${Date.now()}`);
    const getMap = () => {
        if (!refs.current) {
            refs.current = new Map() as FundSelectionCount;
        }
        return refs.current;
    };
    const { updateSelection, stepData, createSubscriptions } =
        useSelectDocumentContext();
    const availableDocSubs = createSubscriptions(doc, {
        funds: fundIds,
        shareClasses: shareClassIds,
    }).map((sub) => ({ ...sub, frequency }));
    const docSubs = stepData.data?.subscriptions
        ? stepData.data.subscriptions.filter((datum) =>
              availableDocSubs.some(
                  (sub) => sub.subscriptionId === datum.subscriptionId,
              ),
          )
        : [];
    const closeModal = () => {
        setIsOpen(false);
        setIsSaved(false);
        setTimeout(() => {
            onClose && onClose();
        }, 200);
    };
    const openModal = () => {
        setIsOpen(true);
        onOpen?.();
    };

    const allSelected = docSubs.length === shareClasses.length;
    const scCount = allSelected ? <Translate>All</Translate> : docSubs.length;
    const fundError = (
        <div className="mb-2 flex gap-2">
            <XCircleIcon
                width={16}
                className="text-core-red mt-0.5 inline shrink-0"
            />
            <p className="text-core-red text-sm">
                <Translate>Please select a share class</Translate>
            </p>
        </div>
    );
    const infoMsg = (
        <div className="flex gap-2 border border-[#175EA126] bg-[#E3F2FD] px-2 py-1.5">
            <InformationCircleIcon
                width={18}
                className="mt-0.5 inline shrink-0 text-[#175EA1]"
            />
            <p className="text-sm text-[#175EA1]">
                <Translate>
                    {`Please select specific share classes${
                        funds.length > 1 ? " for each fund" : ""
                    }`}
                </Translate>
                .
            </p>
        </div>
    );
    const errorMsg = (
        <div className="flex gap-2 border border-[#67121026] bg-[#FFEAEC] px-2 py-1.5">
            <XCircleIcon
                width={18}
                className="text-core-red mt-0.5 inline shrink-0"
            />
            <p className="text-core-red text-sm">
                <Translate>
                    {`Please select at least 1 share class${
                        funds.length > 1 ? " per fund" : ""
                    }`}
                </Translate>
                .
            </p>
        </div>
    );

    const [selectedSubs, setSelectedSubs] = useState(
        allSelected ? [] : docSubs,
    );
    const hasErrors = (id?: string) => {
        const noSelection = getFundsWithNoSelection();
        if (!isSaved || noSelection.length === 0) {
            return false;
        }
        return id ? noSelection.includes(id) : true;
    };
    const getFundsWithNoSelection = () =>
        fundIds.filter((id) => getMap().get(id)?.count === 0);

    const handleChange = (
        subscriptionId: string | string[],
        shareClasses: string | string[],
        selected: boolean,
    ) => {
        const changeSubscriptionIds = Array.isArray(subscriptionId)
            ? subscriptionId
            : [subscriptionId];
        const changeSubscriptions = availableDocSubs.filter((sub) =>
            changeSubscriptionIds.includes(`${sub.subscriptionId}`),
        );
        setTimeout(() => setErrKey(`err-key-${Date.now()}`), 50);
        if (changeSubscriptions.length > 0) {
            setSelectedSubs((current) => {
                onChange?.(shareClasses, selected);
                return selected
                    ? uniqBy(
                          prop<string>("subscriptionId"),
                          current.concat(changeSubscriptions),
                      )
                    : current.filter(
                          (sub) =>
                              !changeSubscriptionIds.includes(
                                  `${sub.subscriptionId}`,
                              ),
                      );
            });
        }
    };
    const handleSave = (subs: SubscribableAsset[]) => () => {
        setIsSaved(true);
        const noSelection = getFundsWithNoSelection();
        if (noSelection.length > 0) {
            return;
        }
        closeModal();
        updateSelection({
            doc,
            subs,
            funds: fundIds,
            shareClasses: shareClassIds,
        });
        onSave?.();
    };

    return (
        <div>
            <button
                className="ml-6 text-xs text-[#004E86] sm:text-sm"
                onClick={openModal}
            >
                {docSubs.length > 1 ? (
                    <Translate insert={scCount} children={"{} share classes"} />
                ) : (
                    <>
                        {scCount} <Translate>share class</Translate>
                    </>
                )}
            </button>

            <Transition appear show={isOpen} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-[2000]"
                    onClose={closeModal}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-hidden sm:overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center text-center sm:p-4">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="max-h-[100dvh] w-full max-w-3xl transform overflow-hidden bg-white text-left align-middle shadow-xl transition-all sm:rounded-md">
                                    <Dialog.Title
                                        as="div"
                                        className="flex justify-between border-b px-6 py-4 text-lg font-medium leading-6 text-gray-900 sm:text-2xl"
                                    >
                                        {doc.name}:{" "}
                                        <Translate>
                                            share class selection
                                        </Translate>
                                        <XMarkIcon
                                            width={24}
                                            className="text-taupe-grey cursor-pointer"
                                            onClick={closeModal}
                                        />
                                    </Dialog.Title>
                                    <div className="inset-0 max-h-[70dvh] w-full overflow-y-auto">
                                        <div className="px-6 py-4">
                                            {funds.map((fund) => {
                                                const fundShareClasses =
                                                    shareClasses.filter((sc) =>
                                                        fund.data.share_classes.includes(
                                                            sc.data.id,
                                                        ),
                                                    );
                                                return (
                                                    <FundShareClassSelect
                                                        key={`fund-${fund.data.id}`}
                                                        fund={fund}
                                                        shareClasses={
                                                            fundShareClasses
                                                        }
                                                        allSubscriptions={availableDocSubs.map(
                                                            (sub) =>
                                                                `${sub.subscriptionId}`,
                                                        )}
                                                        selected={selectedSubs.map(
                                                            (s) =>
                                                                `${s.subscriptionId}`,
                                                        )}
                                                        onChange={handleChange}
                                                        ref={(node?: {
                                                            count: number;
                                                        }) => {
                                                            const map =
                                                                getMap();
                                                            if (node) {
                                                                map.set(
                                                                    `${fund.data.id}`,
                                                                    node,
                                                                );
                                                            } else {
                                                                map.delete(
                                                                    `${fund.data.id}`,
                                                                );
                                                            }
                                                        }}
                                                        error={
                                                            hasErrors(
                                                                `${fund.data.id}`,
                                                            )
                                                                ? fundError
                                                                : null
                                                        }
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center justify-start gap-4 border-t px-6 py-4 shadow-[0_-1px_2px_0_rgba(0,0,0,0.05)] sm:flex-nowrap">
                                        <Button
                                            variant="large"
                                            onClick={handleSave(selectedSubs)}
                                        >
                                            <Translate>Save</Translate>
                                        </Button>
                                        <Button
                                            variant="large"
                                            color="white"
                                            onClick={closeModal}
                                        >
                                            <Translate>Cancel</Translate>
                                        </Button>
                                        <div
                                            key={errKey}
                                            className="-order-1 w-full"
                                        >
                                            {hasErrors() ? errorMsg : infoMsg}
                                        </div>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
}

type FundShareClassSelectParams = {
    fund: FundDataWithType;
    shareClasses: ShareClassDataWithType[];
    allSubscriptions: Array<string>;
    selected?: Array<string>;
    error?: ReactNode;
    onChange?(
        id: string | string[],
        shareClasses: string | string[],
        selected: boolean,
    ): void;
};

const FundShareClassSelect = forwardRef(
    (
        {
            selected,
            fund,
            shareClasses,
            allSubscriptions,
            error,
            onChange,
        }: FundShareClassSelectParams,
        ref,
    ) => {
        const getSubscriptionId = (sc: ShareClassDataWithType) =>
            allSubscriptions.find((subId) => {
                const scId = getShareClassId(sc);
                const [_, entityId] = subId.split("_");
                return entityId === scId;
            });
        const availableSubscriptions = (subId: string) =>
            shareClasses.some((sc) => subId === getSubscriptionId(sc));
        const groupedShareClasses = Object.entries(
            groupShareClasses(shareClasses),
        );
        const [openGroups, setOpenGroups] = useState<number[]>(
            selected?.length
                ? groupedShareClasses
                      .map(([_, scs], i) =>
                          scs.some((sc) =>
                              selected?.includes(getSubscriptionId(sc) ?? ""),
                          )
                              ? i
                              : -1,
                      )
                      .filter((i) => i >= 0)
                : [],
        );

        const selectedFundSCs = selected
            ? selected.filter(availableSubscriptions)
            : [];
        const isAllSelected = selectedFundSCs.length === shareClasses.length;
        const selectToggleClasses =
            "text-sm text-core-blue underline mb-2 font-medium cursor-pointer";
        const handleAllToggle = (select: boolean) => {
            onChange &&
                onChange(
                    allSubscriptions.filter(availableSubscriptions),
                    shareClasses.map(
                        (sc) => sc.data.data.shareclassDescriptor ?? "",
                    ),
                    select,
                );
        };
        const handleToggleGroup = (i: number) => {
            setOpenGroups((open) => {
                if (open.includes(i)) {
                    return open.filter((o) => o !== i);
                } else {
                    return open.concat(i);
                }
            });
        };
        useImperativeHandle(
            ref,
            () => ({
                count: selectedFundSCs.length,
            }),
            [selectedFundSCs.length],
        );
        return (
            <div className="mb-4">
                <h4 className="my-2 text-xl font-medium">
                    {fund.data.data.name}
                </h4>
                {selectedFundSCs.length === 0 && error}
                <SelectAllToggle
                    onLabel={<Translate>Clear all</Translate>}
                    offLabel={<Translate>Select all</Translate>}
                    value={isAllSelected}
                    className={selectToggleClasses}
                    onChange={handleAllToggle}
                />
                {fund.type === "collective" ? (
                    groupedShareClasses.map(([group, grouped], i) => (
                        <ControlledCollapsible
                            key={`group-${group}-${i}`}
                            label={
                                <Translate
                                    insert={group}
                                    children="{} share classes"
                                />
                            }
                            labelClassNames={cx(
                                "border-platinum font-medium w-full flex items-center z-30 relative",
                                i === 0 && "mt-1 sm:mt-0",
                                openGroups.includes(i) && "sm:border-b pb-2",
                            )}
                            className={cx(
                                grouped.length - 1 === i
                                    ? "mb-1 sm:mb-2"
                                    : "mb-2 sm:mb-3",
                            )}
                            withChevron
                            isOpen={openGroups.includes(i)}
                            onClick={() => handleToggleGroup(i)}
                        >
                            <ShareClassCheckboxes
                                shareClasses={
                                    grouped as ShareClassDataWithType[]
                                }
                                selected={selected}
                                onChange={(id, sc, selected) =>
                                    onChange?.(id, sc, selected)
                                }
                                getSubscriptionId={getSubscriptionId}
                            />
                        </ControlledCollapsible>
                    ))
                ) : (
                    <ShareClassCheckboxes
                        shareClasses={shareClasses}
                        selected={selected}
                        onChange={(id, sc, selected) =>
                            onChange?.(id, sc, selected)
                        }
                        getSubscriptionId={getSubscriptionId}
                    />
                )}
            </div>
        );
    },
);

type SCCProps = {
    shareClasses: ShareClassDataWithType[];
    selected?: Array<string>;
    getSubscriptionId(sc: ShareClassDataWithType): string | undefined;
    onChange?(
        id: string | string[],
        shareClass: string,
        selected: boolean,
    ): void;
};

const ShareClassCheckboxes = ({
    shareClasses,
    onChange,
    selected,
    getSubscriptionId,
}: SCCProps) => {
    return shareClasses.map((sc) => {
        const scSubId = getSubscriptionId(sc);
        return (
            <Checkbox
                key={`${scSubId}`}
                value={scSubId}
                label={
                    sc.type === "pmc"
                        ? sc.data.data.iterationName
                        : sc.data.data.shortName
                }
                selected={selected?.includes(`${scSubId}`)}
                onChange={(id, checked) =>
                    onChange?.(
                        `${id}`,
                        sc.data.data.shareclassDescriptor ?? "",
                        checked,
                    )
                }
                labelClassName="text-sm"
            />
        );
    });
};
